if (!customElements.get('wishlist-proxy')) {
  class WishlistProxy extends HTMLElement {
    static OUR_BTN = '.product-wishlist-stub';
    static APP_BTN = '.st-wishlist-button';
    static HEADER_BTN = '.header_whishlist_custom';
    static HEADER_LABEL = '[data-wishlist-label]';
    static HEADER_COUNT = '[data-wishlist-count]';
    static DETAILS_BTN = '.st-wishlist-details-button';
    static APP_LABEL = '.st-label.st-js-label';
    static APP_NUMBER = '.st-number.st-js-number';
    static APP_NUMBER_HIDDEN = 'st-none';
    static COUNT_HIDDEN_CLASS = 'is-hidden';
    static ADDED_CLASS = 'st-is-added';
    static ACTIVE_CLASS = 'is-active';
    static LS_KEY_ANON = 'st-wishlist-local';
    static LS_KEY_LOGGED = 'st-wishlist-logged';
    static ADDED_EVENTS = ['st-added-product', 'swishlist:st-added-product'];
    static DELETED_EVENTS = ['st-deleted-product', 'swishlist:st-deleted-product'];

    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.onAdded = this.onAdded.bind(this);
      this.onDeleted = this.onDeleted.bind(this);
      this.onStorage = this.onStorage.bind(this);
      this.onPageshow = this.onPageshow.bind(this);
      this.onReady = this.onReady.bind(this);
      this.fullSync = this.fullSync.bind(this);

      document.addEventListener('click', this.onClick);
      WishlistProxy.ADDED_EVENTS.forEach((n) => window.addEventListener(n, this.onAdded));
      WishlistProxy.DELETED_EVENTS.forEach((n) => window.addEventListener(n, this.onDeleted));
      window.addEventListener('storage', this.onStorage);
      window.addEventListener('pageshow', this.onPageshow);
      document.addEventListener('SWishlistReady', this.onReady);

      if (document.body.classList.contains('st-wishlist-initialized')) {
        this.defer(this.fullSync);
      }
      this.initialFallbackTimer = setTimeout(this.fullSync, 500);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onClick);
      WishlistProxy.ADDED_EVENTS.forEach((n) => window.removeEventListener(n, this.onAdded));
      WishlistProxy.DELETED_EVENTS.forEach((n) => window.removeEventListener(n, this.onDeleted));
      window.removeEventListener('storage', this.onStorage);
      window.removeEventListener('pageshow', this.onPageshow);
      document.removeEventListener('SWishlistReady', this.onReady);
      clearTimeout(this.initialFallbackTimer);
    }

    defer(fn) {
      queueMicrotask(fn);
      setTimeout(fn, 100);
    }

    onClick(event) {
      const ourBtn = event.target.closest(WishlistProxy.OUR_BTN);
      if (ourBtn) {
        const scope = this.scopeFor(ourBtn);
        const appBtn = scope?.querySelector(WishlistProxy.APP_BTN);
        if (!appBtn) return;
        const form = ourBtn.closest('form');
        const productScope = ourBtn.closest('product-form, product-info, .product');
        const variantInput =
          form?.elements?.namedItem('id') ||
          productScope?.querySelector('input[name="id"]:not([disabled])');
        const variantId = variantInput?.value;
        if (variantId) appBtn.setAttribute('data-variant-id', variantId);
        event.preventDefault();
        event.stopPropagation();
        appBtn.click();
        return;
      }
      const headerBtn = event.target.closest(WishlistProxy.HEADER_BTN);
      if (headerBtn) {
        const detailsBtn = document.querySelector(WishlistProxy.DETAILS_BTN);
        if (!detailsBtn) return;
        event.preventDefault();
        event.stopPropagation();
        detailsBtn.click();
      }
    }

    onAdded(event) {
      const handle = event.detail?.product?.handle;
      this.defer(() => {
        this.syncCardByHandle(handle);
        this.syncHeader();
      });
    }

    onDeleted(event) {
      const handle = event.detail?.productHandle;
      this.defer(() => {
        this.syncCardByHandle(handle);
        this.syncHeader();
      });
    }

    onStorage(event) {
      if (event.key === WishlistProxy.LS_KEY_ANON || event.key === WishlistProxy.LS_KEY_LOGGED) {
        this.defer(this.fullSync);
      }
    }

    onPageshow(event) {
      if (event.persisted) this.defer(this.fullSync);
    }

    onReady() {
      this.defer(this.fullSync);
    }

    fullSync() {
      this.syncCardsInRoot(document);
      this.syncHeader();
    }

    syncCard(ourBtn, appBtn) {
      if (!ourBtn || !appBtn) return;
      const isAdded = appBtn.classList.contains(WishlistProxy.ADDED_CLASS);
      ourBtn.classList.toggle(WishlistProxy.ACTIVE_CLASS, isAdded);
      ourBtn.setAttribute('aria-pressed', String(isAdded));
    }

    scopeFor(btn) {
      return (
        btn.closest('product-card') ||
        btn.closest('product-info') ||
        btn.closest('product-form') ||
        btn.parentElement
      );
    }

    syncCardsInRoot(root) {
      if (!root) return;
      root.querySelectorAll(WishlistProxy.OUR_BTN).forEach((ourBtn) => {
        const scope = this.scopeFor(ourBtn);
        const appBtn = scope?.querySelector(WishlistProxy.APP_BTN);
        this.syncCard(ourBtn, appBtn);
      });
    }

    syncCardByHandle(handle) {
      if (!handle) return;
      const selector = `${WishlistProxy.APP_BTN}[data-handle="${CSS.escape(handle)}"]`;
      document.querySelectorAll(selector).forEach((appBtn) => {
        const scope = this.scopeFor(appBtn);
        scope?.querySelectorAll(WishlistProxy.OUR_BTN).forEach((ourBtn) => this.syncCard(ourBtn, appBtn));
      });
    }

    syncHeader() {
      const detailsBtn = document.querySelector(WishlistProxy.DETAILS_BTN);
      const headerBtn = document.querySelector(WishlistProxy.HEADER_BTN);
      if (!headerBtn || !detailsBtn) return;

      const appColor = detailsBtn.style.color || getComputedStyle(detailsBtn).color;
      if (appColor) headerBtn.style.setProperty('--header-wishlist-color', appColor);

      const labelText = detailsBtn.querySelector(WishlistProxy.APP_LABEL)?.textContent?.trim();
      const ourLabel = headerBtn.querySelector(WishlistProxy.HEADER_LABEL);
      if (labelText && ourLabel && ourLabel.textContent !== labelText) {
        ourLabel.textContent = labelText;
      }

      const appNumber = detailsBtn.querySelector(WishlistProxy.APP_NUMBER);
      const ourCount = headerBtn.querySelector(WishlistProxy.HEADER_COUNT);
      if (appNumber && ourCount) {
        const numberStyle = appNumber.style;
        const numberColor = numberStyle.getPropertyValue('--color').trim();
        const numberBg = numberStyle.getPropertyValue('--bg-color').trim();
        if (numberColor) headerBtn.style.setProperty('--header-wishlist-count-color', numberColor);
        if (numberBg) headerBtn.style.setProperty('--header-wishlist-count-bg', numberBg);
        const countText = appNumber.textContent?.trim() ?? '';
        if (ourCount.textContent !== countText) ourCount.textContent = countText;
        const isHidden = appNumber.classList.contains(WishlistProxy.APP_NUMBER_HIDDEN) || countText === '' || countText === '0';
        ourCount.classList.toggle(WishlistProxy.COUNT_HIDDEN_CLASS, isHidden);
      }
    }
  }

  customElements.define('wishlist-proxy', WishlistProxy);
}
